import { Link } from '@packages/modules/src/Link/Link';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, type JSX } from 'react';
import { useIntl } from 'react-intl';
import type {
  GTMEventGlycerinDisplayLink,
  GTMEventGlycerinDisplayMarketing,
  GTMEventGlycerinLinkClick,
  GTMEventGlycerinMarketingClick,
} from '@packages/tracking/src/types/events';
import { GlycerinMarketingType } from '@packages/tracking/src/types/enums';
import { useAssignLinkTargetBasedOnBotStatus } from '../../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { messages } from '../PromotionBanner.messages';
import type { Promotion } from '../types';
import { PromotionAccordionTemplate } from './PromotionAccordionTemplate.client';
import { classes } from '../../../utils/classes';

export const PromotionAsAccordion = ({
  name,
  title,
  description,
  feedbackDescription,
  cartCode,
  textsCode,
  feedbackTitle,
  isActive,
  isExpanded,
  setIsExpanded,
  setActivePromotionCode,
  isPaybackPromotion,
}: Promotion & {
  isActive: boolean;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  setActivePromotionCode: (
    cartCodeToActivate: string | null,
    textsCodeToActivate: string | null,
    isPaybackPromotion?: Promotion['isPaybackPromotion'],
  ) => Promise<void>;
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const config = useConfig();
  const displayTitle = isActive && feedbackTitle ? feedbackTitle : title;
  const dispatchGTMEvent = useTracking();

  // INSPIRE-3692 - Close the accordion when the user navigates to a different page
  const pathname = usePathname();
  const lastPathnameRef = useRef(pathname);
  useEffect(() => {
    if (lastPathnameRef.current !== pathname) {
      setIsExpanded(false);
      lastPathnameRef.current = pathname;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(formatMessage(messages.newsletterSubscribeLink));

  const linkWasTracked = useRef(false);

  /**
   * Track display of Promobanner
   */
  useEffect(() => {
    dispatchGTMEvent<GTMEventGlycerinDisplayMarketing>({
      event: 'DisplayMarketing',
      DisplayMarketingData: {
        category: 'promobanner',
        detail: isExpanded ? 'open' : 'close',
        marketingType: GlycerinMarketingType.CAMPAIGN,
        campaignType: 'aktion',
        campaignId: `${isPaybackPromotion ? 'PB_' : ''}${cartCode}`,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Track display of Newsletter link visible
   */
  useEffect(() => {
    if (isExpanded && !linkWasTracked.current) {
      dispatchGTMEvent<GTMEventGlycerinDisplayLink>({
        event: 'DisplayLink',
        DisplayLinkData: {
          category: 'promobanner',
          detail: 'nl_anmelde_link',
          label: 'newsletter',
          target: finalLink,
        },
      });
      linkWasTracked.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  return (
    <PromotionAccordionTemplate
      summaryContent={displayTitle}
      isPaybackPromotion={isPaybackPromotion}
      isExpanded={isExpanded}
      setIsExpanded={(newValue) => {
        setIsExpanded(newValue);

        if (!isActive && cartCode) {
          setActivePromotionCode(cartCode, textsCode, isPaybackPromotion);
        }

        dispatchGTMEvent({
          event: 'trackPromoBanner',
          eventLabel: 'promo-banner',
          eventValue: newValue ? `promobanner_open_${name}` : `promobanner_closed1_${name}`,
        });

        dispatchGTMEvent<GTMEventGlycerinMarketingClick>({
          event: 'MarketingClick',
          MarketingClickData: {
            category: 'promobanner',
            detail: newValue ? 'open' : 'close',
            marketingType: GlycerinMarketingType.CAMPAIGN,
            campaignType: 'aktion',
            campaignId: `${isPaybackPromotion ? 'PB_' : ''}${cartCode}`,
          },
        });
      }}
      newsletterLink={finalLink}
      onLinkClick={() => {
        setIsExpanded(false);
        dispatchGTMEvent<GTMEventGlycerinLinkClick>({
          event: 'LinkClick',
          LinkClickData: {
            category: 'promobanner',
            detail: 'nl_anmelde_link',
            label: 'newsletter',
            target: finalLink,
          },
        });
      }}
      detailscontent={
        <div className="flex flex-col gap-2">
          <p
            className={classes(
              'text-size-body-sm',
              config.styleVariant === 'dark' ? 'font-normal' : 'font-bold',
            )}
          >
            {description}
          </p>
          <p className="text-size-body-xs">{feedbackDescription}</p>
          <span className="text-size-body-sm">
            <Link
              className="underline"
              href="/"
              color="inherit"
              onClick={(event) => {
                event.preventDefault();
                setIsExpanded(false);

                dispatchGTMEvent({
                  event: 'trackPromoBanner',
                  eventLabel: 'promo-banner',
                  eventValue: `promobanner_closed1_${name}`,
                });

                dispatchGTMEvent<GTMEventGlycerinLinkClick>({
                  event: 'LinkClick',
                  LinkClickData: {
                    category: 'promobanner',
                    detail: 'use promotion',
                  },
                });
              }}
            >
              {formatMessage(messages.nowShopping)}
            </Link>
          </span>
        </div>
      }
    />
  );
};
